// LocalStorage-backed Chronicle event store.

import { toMinutes, MIN_PER_DAY, addDaysISO } from "./time";
import { scopedKey, getActiveUid } from "./userScope";

function eventsKey() {
  return scopedKey("events.v1");
}

function readAll() {
  try {
    const raw = localStorage.getItem(eventsKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Live (non-soft-deleted) events only. Soft-deleted records are kept in storage
// (and synced to the cloud) for recovery/audit but hidden from every view,
// statistic and AI analysis.
function readLive() {
  return readAll().filter((e) => !e.deletedAt);
}

function writeAll(events) {
  localStorage.setItem(eventsKey(), JSON.stringify(events));
  window.dispatchEvent(new CustomEvent("chronicles:changed"));
}

function nowISO() {
  return new Date().toISOString();
}

export function getAllEvents() {
  return readLive();
}

// Returns render-ready segments for a given day. Cross-midnight events are
// split into two segments: a "continues next" segment on the start day
// (start → midnight) and a "continued prev" segment on the following day
// (midnight → end). The underlying event is never duplicated in storage.
export function getEventsForDate(dateISO) {
  const all = readLive();
  const prevISO = addDaysISO(dateISO, -1);
  const segments = [];

  for (const e of all) {
    if (e.date === dateISO) {
      if (e.crossMidnight) {
        segments.push({
          ...e,
          startMinutes: toMinutes(e.startHour, e.startMinute),
          endMinutes: MIN_PER_DAY,
          _continuesNext: true,
        });
      } else {
        segments.push(decorate(e));
      }
    } else if (e.date === prevISO && e.crossMidnight) {
      segments.push({
        ...e,
        startMinutes: 0,
        endMinutes: toMinutes(e.endHour, e.endMinute),
        _continuedPrev: true,
      });
    }
  }

  return segments.sort((a, b) => a.startMinutes - b.startMinutes);
}

function decorate(e) {
  return {
    ...e,
    startMinutes: toMinutes(e.startHour, e.startMinute),
    endMinutes: toMinutes(e.endHour, e.endMinute),
  };
}

export function createEvent(payload) {
  const id =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const now = nowISO();
  // Future-proof metadata (id, userId, createdAt, updatedAt, version) rides
  // alongside the item and never changes current behaviour.
  const ev = {
    id,
    userId: getActiveUid() || null,
    createdAt: now,
    updatedAt: now,
    version: 1,
    ...payload,
  };
  const next = [...readAll(), ev];
  writeAll(next);
  return decorate(ev);
}

// Soft delete: preserve the record with a deletedAt timestamp instead of
// removing it. Users experience deletion exactly as before (hidden everywhere).
export function deleteEvent(id) {
  const now = nowISO();
  const next = readAll().map((e) =>
    e.id === id
      ? { ...e, deletedAt: now, updatedAt: now, version: (e.version || 1) + 1 }
      : e
  );
  writeAll(next);
}

export function updateEvent(id, patch) {
  const now = nowISO();
  const next = readAll().map((e) =>
    e.id === id
      ? {
          ...e,
          ...patch,
          id,
          userId: e.userId || getActiveUid() || null,
          createdAt: e.createdAt || now,
          updatedAt: now,
          lastEditedAt: now,
          version: (e.version || 1) + 1,
        }
      : e
  );
  writeAll(next);
}

export function getEventById(id) {
  const ev = readAll().find((e) => e.id === id && !e.deletedAt);
  return ev ? decorate(ev) : null;
}

// --- Bulk operations -------------------------------------------------------
// Create many events in a SINGLE localStorage write (one snapshot-sync trigger),
// instead of N individual writes. Idempotent by `sourceEventId`: an event whose
// sourceEventId already exists is skipped, so retries/restarts never duplicate.
// Each event is tagged with `bulkJobId` so a bulk job can be rolled back in
// isolation without touching unrelated events.
export function bulkCreateEvents(drafts) {
  const all = readAll();
  const existingSids = new Set(all.filter((e) => e.sourceEventId).map((e) => e.sourceEventId));
  const now = nowISO();
  const uid = getActiveUid() || null;
  const toAdd = [];
  let skipped = 0;
  for (const d of drafts || []) {
    if (d.sourceEventId && existingSids.has(d.sourceEventId)) { skipped += 1; continue; }
    const id =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    toAdd.push({ id, userId: uid, createdAt: now, updatedAt: now, version: 1, ...d });
    if (d.sourceEventId) existingSids.add(d.sourceEventId);
  }
  if (toAdd.length) writeAll([...all, ...toAdd]);
  return { created: toAdd.length, skipped };
}

// Roll back a bulk job: soft-delete ONLY the live events created by that job.
export function deleteEventsByBulkJob(bulkJobId) {
  if (!bulkJobId) return { removed: 0 };
  const now = nowISO();
  let removed = 0;
  const next = readAll().map((e) => {
    if (e.bulkJobId === bulkJobId && !e.deletedAt) {
      removed += 1;
      return { ...e, deletedAt: now, updatedAt: now, version: (e.version || 1) + 1 };
    }
    return e;
  });
  if (removed) writeAll(next);
  return { removed };
}

export function countEventsByBulkJob(bulkJobId) {
  return readAll().filter((e) => e.bulkJobId === bulkJobId && !e.deletedAt).length;
}

